
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const AboutPage = () => {
  const milestones = [
    {
      year: '2008',
      title: 'Company Founded',
      description: 'Started with a vision to revolutionize medical laboratory services with cutting-edge technology and expert care.',
    },
    {
      year: '2012',
      title: 'ISO Certification',
      description: 'Achieved ISO 9001 certification, establishing our commitment to quality management and continuous improvement.',
    },
    {
      year: '2016',
      title: 'Expansion',
      description: 'Expanded operations to serve 500+ healthcare facilities across the country with 24/7 service capabilities.',
    },
    {
      year: '2024',
      title: 'Innovation Leader',
      description: 'Leading the industry with AI-powered diagnostics and advanced molecular testing capabilities.',
    },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Excellence',
      description: 'Maintaining the highest standards in every test, every time, with rigorous quality control protocols.',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Patient-Centered Care',
      description: 'Putting patient health and safety first in everything we do, with compassionate and reliable service.',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'Continuously investing in cutting-edge technology and research to advance medical diagnostics.',
    },
  ];

  const team = [
    {
      name: 'Dr. Robert Anderson',
      role: 'Chief Medical Officer',
      image: 'https://public.readdy.ai/ai/img_res/bbb875ee0d9af82c7810ceb2471cc333.jpg',
    },
    {
      name: 'Dr. Jennifer Martinez',
      role: 'Laboratory Director',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20scientist%20portrait%20confident%20laboratory%20director%20clean%20white%20background%20high%20quality%20headshot%20experienced%20researcher&width=400&height=400&seq=team2&orientation=squarish',
    },
    {
      name: 'Dr. Michael Chen',
      role: 'Head of Research',
      image: 'https://readdy.ai/api/search-image?query=professional%20male%20scientist%20portrait%20confident%20research%20director%20clean%20white%20background%20high%20quality%20headshot%20experienced%20medical%20professional&width=400&height=400&seq=team3&orientation=squarish',
    },
    {
      name: 'Dr. Sarah Williams',
      role: 'Quality Assurance Director',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20doctor%20portrait%20confident%20quality%20director%20clean%20white%20background%20high%20quality%20headshot%20experienced%20medical%20professional&width=400&height=400&seq=team4&orientation=squarish',
    },
  ];

  const certifications = [
    {
      logo: 'ri-award-line',
      name: 'ISO 9001:2015',
      issuer: 'International Organization for Standardization',
      date: 'Valid until 2026',
    },
    {
      logo: 'ri-shield-check-line',
      name: 'FDA Registration',
      issuer: 'U.S. Food and Drug Administration',
      date: 'Current',
    },
    {
      logo: 'ri-medal-line',
      name: 'CAP Accreditation',
      issuer: 'College of American Pathologists',
      date: 'Valid until 2025',
    },
    {
      logo: 'ri-verified-badge-line',
      name: 'CLIA Certification',
      issuer: 'Centers for Medicare & Medicaid Services',
      date: 'Current',
    },
    {
      logo: 'ri-lock-line',
      name: 'HIPAA Compliance',
      issuer: 'U.S. Department of Health',
      date: 'Certified',
    },
    {
      logo: 'ri-checkbox-circle-line',
      name: 'GMP Certification',
      issuer: 'Good Manufacturing Practice',
      date: 'Valid until 2026',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=professional%20medical%20laboratory%20team%20working%20together%20modern%20healthcare%20facility%20scientists%20collaborating%20clean%20bright%20environment%20high%20quality%20teamwork&width=1920&height=800&seq=about-hero&orientation=landscape)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">About MedLab Pro</h1>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>About Us</span>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <AnimatedSection>
              <div className="w-full h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20medical%20laboratory%20building%20exterior%20professional%20healthcare%20facility%20architecture%20clean%20contemporary%20design%20high%20quality%20medical%20center&width=600&height=500&seq=story1&orientation=portrait"
                  alt="Our Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
                Our Story
              </div>
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                Building Trust Through Excellence Since 2008
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                MedLab Pro was founded with a simple yet powerful mission: to provide healthcare professionals with the most accurate, reliable, and timely laboratory services available. What started as a small facility has grown into one of the region's most trusted medical laboratories.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our commitment to innovation, quality, and patient care has driven us to continuously invest in cutting-edge technology and attract the industry's top talent. Today, we serve over 500 healthcare facilities and process more than 50,000 tests monthly.
              </p>
            </AnimatedSection>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <AnimatedSection className="order-2 lg:order-1">
              <div className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
                Growth & Innovation
              </div>
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                Expanding Horizons in Medical Diagnostics
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our journey has been marked by continuous growth and innovation. From achieving ISO 9001 certification to implementing AI-powered diagnostic systems, we've consistently pushed the boundaries of what's possible in laboratory medicine.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We've expanded our service offerings to include advanced molecular diagnostics, genetic testing, and specialized research capabilities, all while maintaining our core commitment to accuracy and reliability.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.2} className="order-1 lg:order-2">
              <div className="w-full h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=advanced%20medical%20laboratory%20equipment%20modern%20diagnostic%20technology%20scientists%20working%20with%20high%20tech%20machines%20clean%20professional%20environment%20innovation&width=600&height=500&seq=story2&orientation=portrait"
                  alt="Advanced Technology"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </AnimatedSection>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="w-full h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=medical%20laboratory%20team%20collaboration%20professionals%20working%20together%20modern%20healthcare%20facility%20diverse%20scientists%20high%20quality%20teamwork&width=600&height=500&seq=story3&orientation=portrait"
                  alt="Our Team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
                Today
              </div>
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                Leading the Future of Laboratory Medicine
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Today, MedLab Pro stands as a leader in medical laboratory services, combining state-of-the-art technology with unparalleled expertise. Our team of board-certified pathologists, experienced technicians, and dedicated support staff work around the clock to deliver results you can trust.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We continue to invest in research and development, ensuring that we remain at the forefront of diagnostic innovation while maintaining the personal touch and attention to detail that our clients have come to expect.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <AnimatedSection className="py-24 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Our Mission, Vision & Values
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-primary-500 to-royal-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-3xl p-10 shadow-lg text-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-primary-500 to-royal-600 rounded-2xl">
                <i className="ri-focus-3-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To provide healthcare professionals with accurate, reliable, and timely laboratory services that improve patient outcomes and advance medical knowledge.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg text-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-primary-500 to-royal-600 rounded-2xl">
                <i className="ri-eye-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be the most trusted and innovative medical laboratory, setting new standards in diagnostic excellence and patient care across the healthcare industry.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg text-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-primary-500 to-royal-600 rounded-2xl">
                <i className="ri-star-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">Our Promise</h3>
              <p className="text-gray-600 leading-relaxed">
                To deliver exceptional service with unwavering commitment to quality, accuracy, and innovation while treating every patient with dignity and respect.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-primary-100 rounded-2xl">
                    <i className={`${value.icon} text-primary-600 text-3xl`}></i>
                  </div>
                  <h3 className="text-xl font-display font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Timeline */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Our Journey
            </h2>
            <p className="text-lg text-gray-600">Key milestones in our growth and development</p>
          </div>

          <div className="space-y-12">
            {milestones.map((milestone, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-primary-500 to-royal-600 rounded-2xl shadow-lg">
                      <span className="text-2xl font-bold text-white">{milestone.year}</span>
                    </div>
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-2xl p-8">
                    <h3 className="text-2xl font-display font-bold text-gray-900 mb-3">{milestone.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Team Section */}
      <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
              Our Team
            </div>
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Meet Our Expert Leadership
            </h2>
            <p className="text-lg text-gray-600">
              Our team of board-certified professionals brings decades of combined experience in laboratory medicine.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-primary-600 mb-4">{member.role}</p>
                    <div className="flex justify-center space-x-3">
                      <a
                        href="#"
                        className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-full hover:bg-primary-500 hover:text-white transition-all duration-300 cursor-pointer"
                        aria-label="LinkedIn"
                      >
                        <i className="ri-linkedin-fill"></i>
                      </a>
                      <a
                        href="#"
                        className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-full hover:bg-primary-500 hover:text-white transition-all duration-300 cursor-pointer"
                        aria-label="Email"
                      >
                        <i className="ri-mail-line"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Certifications */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Certifications & Accreditations
            </h2>
            <p className="text-lg text-gray-600">
              Our commitment to quality is validated by leading healthcare organizations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <AnimatedSection key={index} delay={index * 0.05}>
                <div className="flex items-center space-x-6 bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-primary-500 hover:shadow-lg transition-all duration-300">
                  <div className="w-20 h-20 flex items-center justify-center flex-shrink-0 bg-primary-50 rounded-xl">
                    <i className={`${cert.logo} text-primary-600 text-3xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{cert.name}</h3>
                    <p className="text-sm text-gray-600 mb-1">{cert.issuer}</p>
                    <p className="text-sm text-primary-600 font-semibold">{cert.date}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-24 bg-gradient-to-r from-primary-600 to-royal-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Join Our Growing Network
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Partner with MedLab Pro and experience the difference of working with a trusted laboratory leader.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="px-10 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-cyan-50 hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              Contact Us Today
            </Link>
            <Link
              to="/services"
              className="px-10 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default AboutPage;
